
import { Component, Vue, Prop, Watch, Ref } from "vue-property-decorator";
import { ScatterChart, ExtractComponentData } from "vue-chart-3";
import { CreateElement, VNode } from "vue";
import {
  IresChartOptions,
  IresDatasetType,
  X_AXIS_ID,
  LEFT_Y_AXIS,
  getDateFromXAxisValue,
  isIresDatasetHighlighted,
} from "@/utils/DryingProgressGraphDataExtractor";
import { DRYING_GRAPH_HIGHLIGHT_DIM_ALPHA } from "@/utils/colors";
import { formatJsDate } from "@/utils/DateFormatter";
import {
  displayValueOf,
  findNearestPlottedPoint,
} from "@/utils/DryingGraphTooltip";
import { chartSeriesColor } from "@/utils/Theme";
import {
  DryingGraphYAxis,
  plotValueToPowerWatts,
  plotValueToPressureHpa,
  plotValueToPrecipitationMm,
  getHumidityUnit,
} from "@/utils/DryingGraphYAxis";

/** A single tooltip entry representing a nearest data point for one dataset. */
interface TooltipEntry {
  datasetIndex: number;
  color: string;
  value: string;
  iresDatasetType: IresDatasetType | undefined;
  /** Pixel x-coordinate of the matched data point on the canvas. */
  px: number;
  /** Pixel y-coordinate of the matched data point on the canvas. */
  py: number;
  /** Absolute x-distance from cursor to this data point (used for deduplication). */
  xDist: number;
  /** X-value of the matched data point (graph units: minutes/hours/days). */
  xValue: number;
  /** Sensor slot label (e.g. "S1"), undefined for non-sensor datasets. */
  slotLabel: string | undefined;
}

/** A labeled group of tooltip entries for display. */
interface TooltipGroup {
  label: string;
  entries: TooltipEntry[];
}

@Component({
  components: { ScatterChart },
})
export default class DryingProgressChartComponent extends Vue {
  @Prop() chartData!: object;
  @Prop({
    default: function() {
      return {};
    },
  })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options!: any;

  /** Dataset border colors to highlight. Null = no highlight (all full opacity). */
  @Prop({ default: null }) highlightColors!: string[] | null;

  /** The plot's height in px; null keeps the chart's default 400 px. */
  @Prop({ default: null }) height!: number | null;

  @Ref() scatterChartRef!: ExtractComponentData<typeof ScatterChart>;

  /** Tooltip element ref, created via render function for detail view. */
  private tooltipEl: HTMLDivElement | null = null;

  /** Whether the chart is currently zoomed in (not at default zoom level). */
  private zoomed = false;

  /** Whether a drag-to-pan gesture is in progress (hides crosshair/tooltip to prevent misalignment). */
  private isDragging = false;

  /** Crosshair state: current mouse X pixel position within the canvas, -1 when inactive. */
  private crosshairX = -1;

  /** Tooltip positioning and data. */
  tooltipVisible = false;
  tooltipX = 0;
  tooltipY = 0;
  tooltipEntries: TooltipEntry[] = [];

  /** Whether this chart instance is in detail view (vs thumbnail). */
  get isDetailView(): boolean {
    return !!this.options?.iresInfo?.allowZoom;
  }

  /** Plugins to pass to the chart instance — crosshair and highlight for detail view only. Zoom is registered globally. */
  get activePlugins() {
    if (!this.isDetailView) return [];
    return [this.crosshairPlugin, this.highlightPlugin];
  }

  /**
   * A finger (or pen) lifting off the chart. Chart.js reports it as "mouseup"; only a touch-made one
   * clears the readout, so a mouse click on desktop leaves the crosshair as before.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isTouchRelease(event: any): boolean {
    const native = event?.native;
    return (
      event?.type === "mouseup" &&
      !!native &&
      (native.type === "touchend" || native.pointerType === "touch" || native.pointerType === "pen")
    );
  }

  /**
   * Custom Chart.js plugin that draws a vertical crosshair line at the cursor
   * and collects nearest-point data for the floating tooltip.
   */
  get crosshairPlugin() {
    return {
      id: "ires-crosshair",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      afterEvent: (chart: any, args: any) => {
        const event = args.event;
        if (!chart.chartArea) return;
        const { left, right, top, bottom } = chart.chartArea;

        if (
          event.type === "mousemove" &&
          !this.isDragging &&
          event.x >= left &&
          event.x <= right &&
          event.y >= top &&
          event.y <= bottom
        ) {
          this.crosshairX = event.x;
          this.updateTooltip(chart, event.x, event.y);
          args.changed = true;
        } else if (event.type === "mouseout" || event.type === "mousemove" || this.isTouchRelease(event)) {
          if (this.crosshairX >= 0) {
            this.crosshairX = -1;
            this.tooltipVisible = false;
            args.changed = true;
          }
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      afterDraw: (chart: any) => {
        if (this.crosshairX < 0) return;

        const { top, bottom } = chart.chartArea;
        const ctx = chart.ctx as CanvasRenderingContext2D;

        // Draw vertical crosshair line
        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = this.$vuetify.theme.dark
          ? "rgba(255, 255, 255, 0.5)"
          : "rgba(0, 0, 0, 0.4)";
        ctx.lineWidth = 1;
        ctx.moveTo(this.crosshairX, top);
        ctx.lineTo(this.crosshairX, bottom);
        ctx.stroke();
        ctx.restore();

        // Draw highlight dots at matched data points (only within chart area)
        const { left: areaLeft, right: areaRight } = chart.chartArea;
        for (const entry of this.tooltipEntries) {
          if (entry.py < top || entry.py > bottom || entry.px < areaLeft || entry.px > areaRight) {
            continue;
          }
          const isWeather = entry.iresDatasetType?.startsWith('weather-');
          ctx.save();
          ctx.beginPath();
          ctx.arc(entry.px, entry.py, 4, 0, Math.PI * 2);

          if (isWeather) {
            // Dotted outline circle for weather (matches dotted plot lines)
            ctx.setLineDash([2, 2]);
            ctx.strokeStyle = entry.color as string;
            ctx.lineWidth = 2;
            ctx.stroke();
          } else {
            // Solid filled circle for sensor/power data
            ctx.fillStyle = entry.color as string;
            ctx.fill();
            ctx.strokeStyle = "#fff";
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }

          ctx.restore();
        }
      },
    };
  }

  /**
   * Finds the nearest data point for each visible dataset at the given
   * cursor X pixel position and updates the tooltip state.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private updateTooltip(chart: any, mouseX: number, mouseY: number) {
    const xScale = chart.scales[X_AXIS_ID];
    if (!xScale) {
      this.tooltipVisible = false;
      return;
    }

    const cursorXValue = xScale.getValueForPixel(mouseX);
    const entries: TooltipEntry[] = [];

    for (let i = 0; i < chart.data.datasets.length; i++) {
      const dataset = chart.data.datasets[i];
      const meta = chart.getDatasetMeta(i);

      if (meta.hidden || !dataset.showLine || !dataset.data || dataset.data.length === 0) {
        continue;
      }

      // No distance limit: see findNearestPlottedPoint. The highlight dot is skipped when the match
      // lies outside the chart area, so a far-away value shows in the tooltip without a stray dot.
      const nearest = findNearestPlottedPoint(dataset.data, cursorXValue);
      if (nearest === null) continue;

      const value = nearest.y;
      if (value === null || value === undefined) continue;

      const dsType: IresDatasetType | undefined = dataset.iresDatasetType;
      // The reported number is the pre-clamp one where the axis clamp moved it, so a sensor that sent
      // −3.5 °C is not reported as the 0 °C it had to be drawn at. `value` still positions the dot.
      const { displayValue, unit } = this.formatTooltipValue(
        dsType,
        Number(displayValueOf(nearest) ?? value),
        dataset.yAxisID
      );

      // Convert data coordinates to pixel coordinates for highlight dots
      const yScale = chart.scales[dataset.yAxisID];
      const px = xScale.getPixelForValue(nearest.x);
      const py = yScale ? yScale.getPixelForValue(value) : 0;

      entries.push({
        datasetIndex: i,
        color: dataset.borderColor || "#000",
        value: `${displayValue} ${unit}`.trim(),
        iresDatasetType: dsType,
        px,
        py,
        xDist: Math.abs(nearest.x - cursorXValue),
        xValue: nearest.x,
        slotLabel: dataset.iresSlotLabel,
      });
    }

    // Deduplicate: when interrupted runs create multiple datasets for the same
    // sensor (same color + type), keep only the entry closest to the cursor.
    const deduped: TooltipEntry[] = [];
    const seen = new Map<string, number>(); // key → index in deduped
    for (const entry of entries) {
      const key = `${entry.color}|${entry.iresDatasetType}`;
      const existingIdx = seen.get(key);
      if (existingIdx !== undefined) {
        if (entry.xDist < deduped[existingIdx].xDist) {
          deduped[existingIdx] = entry;
        }
      } else {
        seen.set(key, deduped.length);
        deduped.push(entry);
      }
    }

    if (deduped.length === 0) {
      this.tooltipVisible = false;
      return;
    }

    this.tooltipEntries = deduped;

    // Position tooltip using fixed viewport coordinates
    const canvas = chart.canvas as HTMLElement;
    const canvasRect = canvas.getBoundingClientRect();

    const viewportX = canvasRect.left + mouseX + 12;
    const viewportY = canvasRect.top + mouseY - 20;

    // Flip to left side if too close to right edge
    const tooltipWidth = 100;
    if (viewportX + tooltipWidth > window.innerWidth) {
      this.tooltipX = viewportX - tooltipWidth - 24;
    } else {
      this.tooltipX = viewportX;
    }
    this.tooltipY = Math.max(0, viewportY);
    this.tooltipVisible = true;
  }

  /**
   * Converts a plotted value back to its real-world display value and unit.
   * Uses shared reverse-conversion functions from DryingGraphYAxis.ts so tooltip
   * values match what the axis tick labels show.
   */
  private formatTooltipValue(
    dsType: IresDatasetType | undefined,
    plotValue: number,
    yAxisID: string,
  ): { displayValue: string; unit: string } {
    const fmt = (v: number, decimals = 1) => v.toLocaleString(undefined, { maximumFractionDigits: decimals });
    const rightYAxis = this.options?.iresInfo?.rightYAxis;

    switch (dsType) {
      case 'sensor-temp':
      case 'weather-temp':
      case 'panel-temp':
        return { displayValue: fmt(plotValue), unit: "°C" };

      case 'sensor-humidity':
        return { displayValue: fmt(plotValue), unit: rightYAxis != null ? getHumidityUnit(rightYAxis) : "%" };

      case 'weather-humidity':
        return { displayValue: fmt(plotValue), unit: rightYAxis != null ? getHumidityUnit(rightYAxis) : "%" };

      case 'sensor-resistance':
        return { displayValue: fmt(plotValue), unit: "digits" };

      case 'weather-pressure': {
        const realHpa = rightYAxis != null ? plotValueToPressureHpa(plotValue, rightYAxis) : plotValue + 950;
        return { displayValue: fmt(realHpa, 0), unit: "hPa" };
      }

      case 'weather-precipitation': {
        const realMm = rightYAxis != null ? plotValueToPrecipitationMm(plotValue, rightYAxis) : plotValue;
        return { displayValue: fmt(realMm), unit: "mm" };
      }

      case 'power': {
        const realW = rightYAxis != null ? plotValueToPowerWatts(plotValue, rightYAxis) : (plotValue / 100) * 3600;
        return { displayValue: fmt(realW, 0), unit: "W" };
      }

      case 'heat-ratio':
        return { displayValue: fmt(plotValue), unit: "%" };

      default:
        // Fallback for datasets without iresDatasetType
        return { displayValue: fmt(plotValue), unit: yAxisID !== LEFT_Y_AXIS
          ? (rightYAxis != null ? getHumidityUnit(rightYAxis) : "%")
          : "°C" };
    }
  }

  /** Whether the current chart is a panel (intensity) chart rather than a ComBox (humidity) chart. */
  private get isPanel(): boolean {
    return this.options?.iresInfo?.rightYAxis === DryingGraphYAxis.PANEL_INTENSITY;
  }

  /** Tooltip entries grouped by: sensor temperature, sensor humidity/intensity, weather, power — with labels. */
  get groupedTooltipEntries(): TooltipGroup[] {
    const sensorTemp = this.tooltipEntries.filter(e =>
      e.iresDatasetType === 'sensor-temp' || e.iresDatasetType === 'panel-temp'
    );
    const sensorHumidityOrIntensity = this.tooltipEntries.filter(e =>
      e.iresDatasetType === 'sensor-humidity' ||
      e.iresDatasetType === 'sensor-resistance' ||
      e.iresDatasetType === 'heat-ratio'
    );
    const weather = this.tooltipEntries.filter(e =>
      e.iresDatasetType === 'weather-temp' ||
      e.iresDatasetType === 'weather-humidity' ||
      e.iresDatasetType === 'weather-pressure' ||
      e.iresDatasetType === 'weather-precipitation'
    );
    const power = this.tooltipEntries.filter(e => e.iresDatasetType === 'power');
    const other = this.tooltipEntries.filter(e =>
      e.iresDatasetType === undefined
    );

    const rightAxisLabel = this.isPanel
      ? this.$t('dryingGraph.tooltipIntensity') as string
      : this.options?.iresInfo?.rightYAxis === DryingGraphYAxis.COM_BOX_RESISTANCE_DIGITS
        ? this.$t('dryingGraph.tooltipSensorResistance') as string
        : this.$t('dryingGraph.tooltipSensorHumidity') as string;

    const groups: Array<{ label: string; entries: TooltipEntry[] }> = [
      { label: this.$t('dryingGraph.tooltipSensorTemperature') as string, entries: sensorTemp },
      { label: rightAxisLabel, entries: sensorHumidityOrIntensity },
      { label: this.$t('dryingGraph.tooltipWeather') as string, entries: weather },
      { label: this.$t('dryingGraph.tooltipPower') as string, entries: power },
      { label: '', entries: other },
    ];

    return groups.filter(g => g.entries.length > 0);
  }

  /** Formatted timestamp for the tooltip, derived from the closest data point's x-value. */
  get tooltipTimestamp(): string | null {
    if (this.tooltipEntries.length === 0) return null;
    const startDate: Date | undefined = this.options?.iresInfo?.startDate;
    const timeScale = this.options?.iresInfo?.timeScale;
    if (!startDate || !timeScale) return null;
    // Use the entry with the smallest xDist (closest to cursor)
    const closest = this.tooltipEntries.reduce((a, b) => a.xDist < b.xDist ? a : b);
    const date = getDateFromXAxisValue(startDate, closest.xValue, timeScale);
    return formatJsDate(date);
  }

  get tooltipStyle() {
    return {
      left: `${this.tooltipX}px`,
      top: `${this.tooltipY}px`,
    };
  }

  get optionsToDisplay(): IresChartOptions {
    if (!this.isDetailView) {
      // Thumbnails: return options as-is, disable zoom, only add animation callback
      return {
        ...this.options,
        animation: {
          ...this.options.animation,
          onComplete: () => {
            this.emitChartArea();
          },
        },
        plugins: {
          ...this.options.plugins,
          zoom: {
            zoom: { wheel: { enabled: false }, pinch: { enabled: false } },
            pan: { enabled: false },
          },
        },
      };
    }

    // Detail view: full interactive options
    return {
      ...this.options,
      events: ["mousemove", "mouseout", "click", "touchstart", "touchmove", "touchend", "pointerdown", "pointermove", "pointerup", "wheel"],
      hover: {
        mode: "nearest" as const,
        intersect: false,
      },
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        ...this.options.animation,
        onComplete: () => {
          this.emitChartArea();
        },
      },
      plugins: {
        ...this.options.plugins,
        zoom: {
          pan: {
            enabled: true,
            mode: "x" as const,
            onPanStart: ({ chart }: { chart: { canvas: HTMLCanvasElement } }) => {
              if (!this.zoomed) return false; // Block pan when not zoomed
              this.isDragging = true;
              this.crosshairX = -1;
              this.tooltipVisible = false;
              this.tooltipEntries = [];
              chart.canvas.style.cursor = "grabbing";
            },
            onPanComplete: ({ chart }: { chart: { getZoomLevel(): number } }) => {
              this.isDragging = false;
              this.updateZoomState(chart.getZoomLevel());
              this.emitVisibleRange(chart);
              this.updateCanvasCursor();
            },
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: "x" as const,
            onZoomStart: () => {
              this.crosshairX = -1;
              this.tooltipVisible = false;
              this.tooltipEntries = [];
            },
            onZoomComplete: ({ chart }: { chart: { getZoomLevel(): number } }) => {
              this.updateZoomState(chart.getZoomLevel());
              this.emitVisibleRange(chart);
            },
          },
          limits: {
            [X_AXIS_ID]: {
              min: "original" as const,
              max: "original" as const,
              minRange: Math.max(1, ((this.options?.scales?.[X_AXIS_ID]?.max ?? 0) - (this.options?.scales?.[X_AXIS_ID]?.min ?? 0)) * 0.05),
            },
          },
        },
      },
    };
  }

  /**
   * Updates the internal zoom state and notifies the parent.
   * Called by the zoom plugin after every zoom/pan operation.
   */
  private updateZoomState(zoomLevel: number) {
    const isNowZoomed = zoomLevel > 1.01;
    if (isNowZoomed !== this.zoomed) {
      this.zoomed = isNowZoomed;
      this.$emit("zoomStateChanged", this.zoomed);
      this.updateCanvasCursor();
    }
  }

  /** Sets the canvas cursor based on zoom state: grab when zoomed (pan-ready), crosshair otherwise. */
  private updateCanvasCursor() {
    const canvas = this.scatterChartRef?.chartInstance?.canvas as HTMLCanvasElement | undefined;
    if (!canvas) return;
    canvas.style.cursor = this.zoomed ? "grab" : "crosshair";
  }

  /** Resets zoom to the original scale range. */
  resetZoom() {
    const chart = this.scatterChartRef?.chartInstance;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (chart && (chart as any).resetZoom) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (chart as any).resetZoom();
    }
    this.zoomed = false;
    this.$emit("zoomStateChanged", false);
    this.$emit("visibleRangeChanged", null);
    this.updateCanvasCursor();
  }

  @Watch("options")
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onOptionChanged(after: any, before: any) {
    const idBefore = before?.iresInfo?.id;
    const idAfter = after?.iresInfo?.id;

    if (idBefore !== idAfter) {
      // Program chain changed — reset zoom
      this.$nextTick(() => this.resetZoom());
    }
  }

  /**
   * Chart.js plugin that dims non-highlighted datasets by reducing globalAlpha.
   * Reads highlightColors reactively — no stored state, so immune to data rebuild races.
   */
  get highlightPlugin() {
    return {
      id: 'ires-highlight',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      beforeDatasetDraw: (chart: any, args: any) => {
        if (!this.highlightColors || this.highlightColors.length === 0) return;
        const dataset = chart.data.datasets[args.index];
        if (!dataset?.showLine) return;
        chart.ctx.save();
        if (
          isIresDatasetHighlighted(
            dataset.borderColor ?? '',
            dataset.iresDatasetType ?? '',
            this.highlightColors
          )
        ) {
          chart.ctx.restore();
        } else {
          chart.ctx.globalAlpha = DRYING_GRAPH_HIGHLIGHT_DIM_ALPHA;
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      afterDatasetDraw: (chart: any, args: any) => {
        if (!this.highlightColors || this.highlightColors.length === 0) return;
        const dataset = chart.data.datasets[args.index];
        if (!dataset?.showLine) return;
        chart.ctx.restore();
      },
    };
  }

  @Watch("highlightColors")
  onHighlightColorsChanged() {
    if (!this.isDetailView) return;
    const chart = this.scatterChartRef?.chartInstance;
    if (!chart) return;
    // Stash the hovered state on the chart so the scriptable band-fill gradient
    // can dim itself in step with the line dim (the plugin's globalAlpha can't
    // reach the fill, which the built-in Filler paints first).
    (chart as unknown as { $iresHighlightColors?: string[] | null }).$iresHighlightColors =
      this.highlightColors;
    chart.update('none');
  }

  /** Reactive dark-theme flag (reads the Vuetify theme reactively). */
  get isDarkTheme(): boolean {
    return this.$vuetify.theme.dark;
  }

  /**
   * Re-theme the chart on a live theme toggle. The theme-aware chart options
   * (axis tick + grid colors, region fills, phase-label colors) are scriptable
   * and read the live theme, so a cheap update() re-evaluates them instantly —
   * no re-extraction/refetch, so the chart flips in sync with the rest of the UI.
   *
   * Series line colors are static strings baked at extraction time (kept as
   * strings so the legend-hover highlight matching can compare them). Datasets
   * built from the themed sensor palette carry their un-themed `iresBaseColor`,
   * so we re-derive `borderColor` from it here — keeping graph and legend color
   * strings identical for the (now-live) theme. Weather/power lines have no
   * `iresBaseColor` and keep their fixed color, matching their light-mode look.
   */
  @Watch("isDarkTheme")
  onThemeChanged() {
    const chart = this.scatterChartRef?.chartInstance;
    if (!chart) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const dataset of chart.data.datasets as any[]) {
      if (dataset.iresBaseColor) {
        dataset.borderColor = chartSeriesColor(dataset.iresBaseColor);
      }
    }
    chart.update('none');
  }

  private resizeTimer: number | null = null;

  mounted() {
    window.addEventListener("resize", this.onWindowResize);
    // Emit chart area after the chart has had time to render
    this.$nextTick(() => {
      setTimeout(() => {
        this.emitChartArea();
        if (this.isDetailView) this.updateCanvasCursor();
      }, 300);
    });
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.onWindowResize);
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
  }

  private onWindowResize = () => {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.resizeTimer = window.setTimeout(() => {
      this.emitChartArea();
    }, 250) as unknown as number;
  };

  /**
   * Emits the chart area left/right pixel offsets so parent components
   * can align overlays (e.g. timeline markers) with the x-axis, and its top
   * offset so the axis titles above the plot can sit right on it.
   */
  private emitChartArea() {
    const chart = this.scatterChartRef?.chartInstance;
    if (!chart?.chartArea) return;
    const { left, right, top } = chart.chartArea;
    const width = (chart as { width?: number }).width ?? 0;
    // Mid-resize the area can be half laid out; overlays drawn from it got NaN coordinates.
    if (!Number.isFinite(left) || !Number.isFinite(right) || right <= left) return;
    this.$emit("chartAreaUpdated", { left, right, width, top });
    this.emitVisibleRange(chart);
  }

  /**
   * Emits the currently visible x-axis range and the conversion info needed
   * to translate dates into x-axis positions. Fires on initial render,
   * after zoom/pan, and on window resize.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private emitVisibleRange(chart: any) {
    const startDate: Date | undefined = this.options?.iresInfo?.startDate;
    const timeScale = this.options?.iresInfo?.timeScale;
    const xScale = chart.scales?.[X_AXIS_ID];
    if (!startDate || !timeScale || !xScale) return;
    // Mid-resize the scale can be half laid out; the strip and markers placed their days at NaN.
    if (!Number.isFinite(xScale.min) || !Number.isFinite(xScale.max)) return;
    this.$emit("visibleRangeChanged", {
      xMin: xScale.min,
      xMax: xScale.max,
      startDate,
      timeScale,
    });
  }

  /**
   * Render function: thumbnails get ScatterChart as root (no wrapper div),
   * detail view gets a wrapper div with ScatterChart + tooltip overlay.
   */
  render(h: CreateElement): VNode {
    const scatterChart = h(ScatterChart, {
      ref: "scatterChartRef",
      props: {
        chartData: this.chartData,
        options: this.optionsToDisplay,
        plugins: this.activePlugins,
        // A responsive chart takes its container's height, not the canvas attribute.
        ...(this.height ? { styles: { height: `${this.height}px` } } : {}),
      },
    });

    // Thumbnails: render ScatterChart directly as root (original behavior)
    if (!this.isDetailView) {
      return scatterChart;
    }

    // Detail view: wrap in positioned div for tooltip
    const children: VNode[] = [scatterChart];

    // Tooltip overlay
    if (this.tooltipVisible && this.tooltipEntries.length > 0) {
      const groups = this.groupedTooltipEntries.map((group, groupIndex) => {
        const children: VNode[] = [];
        if (group.label) {
          children.push(h("div", { class: "tooltip-label" }, group.label));
        }
        group.entries.forEach((entry) => {
          const isWeather = entry.iresDatasetType?.startsWith('weather-');
          const dotStyle = isWeather
            ? { borderColor: entry.color, borderStyle: 'dashed' }
            : { backgroundColor: entry.color };
          const dotClass = isWeather ? 'tooltip-dot tooltip-dot-weather' : 'tooltip-dot';
          const valueChildren: VNode[] = [
            h("span", { class: dotClass, style: dotStyle }),
          ];
          if (entry.slotLabel) {
            valueChildren.push(h("span", { class: "tooltip-slot" }, entry.slotLabel));
          }
          valueChildren.push(h("span", { class: "tooltip-value" }, entry.value));
          children.push(
            h("div", { key: entry.datasetIndex, class: "tooltip-entry" }, valueChildren)
          );
        });
        return h("div", { key: groupIndex, class: { "tooltip-group": groupIndex > 0 } }, children);
      });

      // Timestamp row at the bottom
      if (this.tooltipTimestamp) {
        groups.push(
          h("div", { class: "tooltip-timestamp" }, this.tooltipTimestamp)
        );
      }

      children.push(
        h("div", {
          class: "crosshair-tooltip",
          style: {
            left: `${this.tooltipX}px`,
            top: `${this.tooltipY}px`,
          },
        }, groups)
      );
    }

    return h("div", { class: "drying-graph-wrapper" }, children);
  }
}
